import React from 'react';
import { Chip, Box } from '@mui/material';
import { styles } from './MilestoneAccordion.styles';
import { ChipSize, ChipVariant, MilestoneChipData } from './types';

interface MilestoneChipsProps {
     milestone: any;
     getMilestoneChipData: (milestone: any) => MilestoneChipData | null;
     areAllTasksChecked: boolean;
     hasMilestoneSyncProgress?: boolean;
     hasMilestoneUnsavedChanges?: boolean;
     workflowTasksCount?: number;
     size?: ChipSize;
     variant?: ChipVariant;
     sx?: any;
}

// Constants
const CHIP_LABELS = {
     UNSAVED_CHANGES: 'Unsaved Changes',
     NOT_APPLICABLE: 'Not Applicable',
} as const;

export const MilestoneChips: React.FC<MilestoneChipsProps> = ({ 
     milestone, 
     getMilestoneChipData, 
     areAllTasksChecked, 
     hasMilestoneSyncProgress, 
     hasMilestoneUnsavedChanges, 
     workflowTasksCount, 
     size = 'small', 
     variant = 'filled', 
     sx 
}) => {
     const chipData = getMilestoneChipData(milestone);
     if (!chipData) return null;

     const chips = [];

     // Unsaved Changes chip
     if (hasMilestoneUnsavedChanges) {
          chips.push(
               <Chip
                    key="unsaved-changes"
                    label={CHIP_LABELS.UNSAVED_CHANGES}
                    size={size}
                    color="info"
                    variant={variant}
                    sx={{ backgroundColor: '#1976d2', color: '#ffffff', ...sx }}
               />
          );
     }

     // Draft chips
     if (chipData.draftCount > 0) {
          chips.push(
               <Chip
                    key="draft"
                    label={`${chipData.draftCount} saved`}
                    size={size}
                    color="default"
                    variant={variant}
                    sx={{ ...styles.draftChip, ...sx }}
               />
          );
     }

     // Saved chips
     if (chipData.savedCount > 0) {
          chips.push(
               <Chip
                    key="saved"
                    label={`${chipData.savedCount} saved`}
                    size={size}
                    color="success"
                    variant={variant}
                    sx={{ backgroundColor: '#2e7d32', color: '#ffffff', ...sx }}
               />
          );
     }

     // Released chips - only show if not all tasks are not applicable
     if (chipData.releasedCount > 0 && chipData.notApplicableCount === 0) {
          chips.push(
               <Chip
                    key="released"
                    label={`${chipData.releasedCount} applicable`}
                    size={size}
                    color="success"
                    variant={variant}
                    sx={{ ...styles.releasedChip, ...sx }}
               />
          );
     }

     // Not Applicable chips
     if (chipData.notApplicableCount > 0) {
          chips.push(
               <Chip
                    key="not-applicable"
                    label={`${chipData.notApplicableCount} not applicable`}
                    size={size}
                    color="error"
                    variant={variant}
                    sx={{ ...styles.taskChip, ...sx }}
               />
          );
     }

     if (chips.length === 0) return null;

     return (
          <Box sx={styles.chipContainer}>
               {chips}
          </Box>
     );
};